import React from 'react'
import { Box, Typography, Button, Card } from '@mui/material'
import { AssessmentTemplate } from '../../types/assessments'

interface AssessmentCardProps {
  template: AssessmentTemplate | any
  onPreview?: () => void
  onUseTemplate?: () => void
  isSelected?: boolean
}

export const AssessmentCard: React.FC<AssessmentCardProps> = ({
  template,
  onPreview,
  onUseTemplate,
  isSelected = false
}) => {
  const handleUseTemplate = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onUseTemplate) {
      onUseTemplate()
    }
  }

  const handleCardClick = () => {
    if (onPreview) {
      onPreview()
    }
  }
  // Get icon component based on template icon
  const renderIcon = () => {
    // Check if icon is a FontAwesome class (starts with 'fa-')
    const isFontAwesome =
      template.icon && typeof template.icon === 'string' && template.icon.startsWith('fa-')

    if (isFontAwesome) {
      // Check if icon already has a style prefix (fas, far, fab, etc.)
      const hasPrefix =
        template.icon.startsWith('fas ') ||
        template.icon.startsWith('far ') ||
        template.icon.startsWith('fab ') ||
        template.icon.startsWith('fal ')

      // Use 'fas' (solid) as default prefix for FontAwesome 5/6 icons
      const iconClass = hasPrefix ? template.icon : `fas ${template.icon}`

      return (
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 2,
            backgroundColor: template.color || '#3498db',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            boxShadow: `0 2px 8px ${template.color}40`
          }}
        >
          <i className={iconClass} style={{ fontSize: '28px', color: '#fff' }}></i>
        </Box>
      )
    }

    // Handle emoji icons (legacy support)
    if (template.icon === '🛡️') {
      return (
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 2,
            backgroundColor: template.color || '#e74c3c',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            border: '2px solid',
            borderColor: template.color || '#e74c3c',
            position: 'relative',
            boxShadow: `0 2px 8px ${template.color}40`
          }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontSize: '36px',
              fontWeight: 'bold',
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1
            }}
          >
            D
          </Typography>
        </Box>
      )
    }

    // Default: render as emoji or text
    return (
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: 2,
          backgroundColor: template.color || '#3498db',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          fontSize: '40px',
          boxShadow: `0 2px 8px ${template.color}40`
        }}
      >
        {template.icon || '📝'}
      </Box>
    )
  }

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        minHeight: '420px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        border: isSelected ? `2px solid ${template.color}` : '1px solid #e0e0e0',
        backgroundColor: '#fff',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
        },
        position: 'relative',
        overflow: 'visible'
      }}
    >
      <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '420px' }}>
        {/* Icon */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>{renderIcon()}</Box>

        {/* Title */}
        <Typography
          variant='h5'
          sx={{
            fontWeight: 700,
            mb: 1,
            textAlign: 'center',
            color: '#212121',
            fontSize: '1.25rem',
            lineHeight: 1.3
          }}
        >
          {template.title}
        </Typography>

        {/* Subtitle */}
        <Typography
          variant='body2'
          sx={{
            mb: 2,
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.875rem',
            lineHeight: 1.4
          }}
        >
          {template.subtitle}
        </Typography>

        {/* Assessment Details */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            mb: 2,
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                backgroundColor: `${template.color}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 0.5
              }}
            >
              <Typography sx={{ color: template.color, fontSize: '16px', fontWeight: 'bold' }}>
                ✓
              </Typography>
            </Box>
            <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {template.questions?.length || 0} Question
              {template.questions?.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                backgroundColor: `${template.color}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 0.5
              }}
            >
              <Typography sx={{ color: template.color, fontSize: '16px' }}>⏱</Typography>
            </Box>
            <Typography variant='caption' sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              5 min
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        <Typography
          variant='body2'
          sx={{
            mb: 3,
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.813rem',
            flexGrow: 1,
            lineHeight: 1.5
          }}
        >
          {template.description}
        </Typography>

        {/* Use Template Button */}
        <Button
          variant='contained'
          fullWidth
          onClick={handleUseTemplate}
          sx={{
            background: `linear-gradient(180deg, ${template.color} 0%, ${template.color}dd 100%)`,
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.813rem',
            textTransform: 'uppercase',
            py: 1.5,
            borderRadius: 2,
            boxShadow: `0 2px 4px ${template.color}40`,
            '&:hover': {
              background: `linear-gradient(180deg, ${template.color}dd 0%, ${template.color} 100%)`,
              boxShadow: `0 4px 8px ${template.color}50`
            }
          }}
        >
          USE TEMPLATE
        </Button>
      </Box>
    </Card>
  )
}
